import React, { useState } from 'react';
import { ExternalLink, ChevronDown, ChevronRight } from 'lucide-react';
import { Citation } from '../utils/api';

interface CitationListProps {
  sources: Citation[];
  className?: string;
}

const CitationList: React.FC<CitationListProps> = ({ sources, className = '' }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!sources || sources.length === 0) {
    return null;
  }

  // Deduplicate sources by URL as a safety measure
  const uniqueSources = sources.filter((source, index, arr) =>
    arr.findIndex(s => s.url === source.url) === index
  );

  // Limit to maximum 7 sources for better UX
  const displaySources = uniqueSources.slice(0, 7);

  const getAuthorityBadge = (authority: string) => {
    const badges = {
      'NCBI': 'bg-blue-100 text-blue-800 border-blue-200',
      'NICE': 'bg-green-100 text-green-800 border-green-200',
      'WHO': 'bg-purple-100 text-purple-800 border-purple-200',
      'CDC': 'bg-red-100 text-red-800 border-red-200',
      'FDA': 'bg-orange-100 text-orange-800 border-orange-200',
    };

    return badges[authority as keyof typeof badges] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getAuthorityIcon = (authority: string) => {
    const icons = {
      'NCBI': '🧬',
      'NICE': '🏛️',
      'WHO': '🌐',
      'CDC': '🏥',
      'FDA': '⚕️',
    };

    return icons[authority as keyof typeof icons] || '📋';
  };

  return (
    <div className={`mt-3 ${className}`}>
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center space-x-2 text-xs font-medium text-gray-600 hover:text-gray-800 transition-colors"
      >
        {isExpanded ? (
          <ChevronDown className="w-3 h-3" />
        ) : (
          <ChevronRight className="w-3 h-3" />
        )}
        <span>
          {displaySources.length} {displaySources.length === 1 ? 'source' : 'sources'}
        </span>
      </button>

      {isExpanded && (
        <div className="space-y-2 mt-2">
          {displaySources.map((source, index) => (
            <div
              key={index}
              className="flex items-start space-x-2 p-2 bg-gray-50 rounded-md border border-gray-200"
            >
              <span className="text-lg flex-shrink-0">
                {getAuthorityIcon(source.authority)}
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-2 mb-1">
                  <span
                    className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getAuthorityBadge(
                      source.authority
                    )}`}
                  >
                    {source.authority}
                  </span>
                  <span className="text-xs text-gray-500 truncate">
                    {source.domain}
                  </span>
                </div>
                <a
                  href={source.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-blue-700 hover:text-blue-900 hover:underline flex items-center space-x-1 group"
                >
                  <span className="truncate">{source.title}</span>
                  <ExternalLink className="w-3 h-3 flex-shrink-0 opacity-60 group-hover:opacity-100" />
                </a>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CitationList;